import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskLines } from "@/components/Motion";
import { sendContact } from "@/lib/api";
import { contact, brand } from "@/config/site";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", inquiry_type: contact.inquiryTypes[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete every field.");
      return;
    }
    setLoading(true);
    try {
      const res = await sendContact({ ...form, name: form.name.trim(), message: form.message.trim() });
      setDone(true);
      toast.success(res.message || "Your message is with us.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something interrupted the message. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border-b border-dh-cream/20 bg-transparent px-1 py-3 font-body text-base text-dh-cream placeholder:text-dh-muted/70 outline-none transition-colors duration-300 focus:border-dh-gold";

  return (
    <section data-testid="contact-page" className="min-h-screen bg-dh-bg pt-36 pb-28 sm:pt-48 sm:pb-36">
      <div className="mx-auto max-w-[1100px] px-5 sm:px-8">
        <div className="grid gap-14 md:grid-cols-12 md:gap-20">
          <div className="md:col-span-5">
            <Reveal>
              <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">Contact</p>
            </Reveal>
            <h1 className="mt-6 font-display text-5xl text-dh-cream sm:text-7xl">
              <MaskLines lines={["Let’s", "Connect."]} />
            </h1>
            <Reveal delay={0.2}>
              <a
                href={`mailto:${brand.contactEmail}`}
                data-testid="contact-email-link"
                className="mt-10 inline-block font-serif-soft text-xl italic text-dh-cream/80 transition-colors hover:text-dh-gold"
              >
                {brand.contactEmail}
              </a>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-6 max-w-sm font-body text-sm leading-relaxed text-dh-muted">{contact.note}</p>
            </Reveal>
          </div>

          <div className="md:col-span-7">
            {done ? (
              <div
                data-testid="contact-success"
                className="flex h-full flex-col justify-center border border-dh-gold/30 bg-dh-forest/20 p-10 text-center"
              >
                <p className="font-display text-3xl text-dh-cream">Thank you.</p>
                <p className="mt-3 font-body text-base text-dh-muted">Your message is with us. We read every one.</p>
              </div>
            ) : (
              <Reveal delay={0.15}>
                <form onSubmit={submit} data-testid="contact-form" noValidate className="space-y-8">
                  <div className="grid gap-8 sm:grid-cols-2">
                    <div>
                      <label htmlFor="c-name" className="mb-2 block font-body text-[11px] uppercase tracking-[0.2em] text-dh-muted">
                        Name
                      </label>
                      <input id="c-name" data-testid="contact-name" type="text" autoComplete="name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
                    </div>
                    <div>
                      <label htmlFor="c-email" className="mb-2 block font-body text-[11px] uppercase tracking-[0.2em] text-dh-muted">
                        Email
                      </label>
                      <input id="c-email" data-testid="contact-email" type="email" autoComplete="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="c-type" className="mb-2 block font-body text-[11px] uppercase tracking-[0.2em] text-dh-muted">
                      Inquiry Type
                    </label>
                    <select
                      id="c-type"
                      data-testid="contact-inquiry-type"
                      value={form.inquiry_type}
                      onChange={set("inquiry_type")}
                      className={`${inputCls} appearance-none cursor-pointer`}
                    >
                      {contact.inquiryTypes.map((t) => (
                        <option key={t} value={t} className="bg-dh-bg2 text-dh-cream">
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="c-message" className="mb-2 block font-body text-[11px] uppercase tracking-[0.2em] text-dh-muted">
                      Message
                    </label>
                    <textarea id="c-message" data-testid="contact-message" rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tell us what brings you to Dhamma." />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={loading}
                    className="group inline-flex items-center justify-center gap-3 border border-dh-gold/70 bg-transparent px-8 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-dh-gold transition-[background-color,color] duration-500 hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-50"
                  >
                    {loading ? "Sending…" : "Send Message"}
                    <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
